import {
  Grid,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { ArrowForward, ListAlt } from '@mui/icons-material'
import useStyles from './styles'
import { isArray, isEmpty } from 'lodash'
import { checkNull } from '../../Helpers'
import { useTheme } from '@emotion/react'

const formatAmount = (amount, symbol) => {
  if (checkNull(amount?.toString())) return `${symbol}0`
  const num = Number(amount)
  if (Number.isInteger(num)) {
    return `${symbol}${num.toLocaleString('en-IN')}`
  }
  return `${symbol}${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const formatAmt = (amt) => {
  if (checkNull(amt?.toString())) return '0'
  const num = Number(amt)
  return num.toLocaleString('en-IN', {
    minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
    maximumFractionDigits: 2,
  })
}

const PricingBreakdown = (props) => {
  const {
    finalAmount,
    paymentData,
    chargeData,
    primaryColor,
    linearColorsName,
    isGradientButton,
    changeBtnText,
    onClick,
    currency_symbol,
    productDetails,
    pl_code,
    merchantName,
    merchantLogo,
    order_details,
  } = props

  const styles = useStyles()
  const theme = useTheme()
  const sm = useMediaQuery(theme.breakpoints.down('sm'))
  const lg = useMediaQuery(theme.breakpoints.down('lg'))

  const themeColor = primaryColor || '#0068EF'
  const isUpdate = paymentData?.updateSubscription && !paymentData?.payThroughRecurrence
  const showAmountInBtn = !isUpdate && !pl_code && changeBtnText !== 'Proceed'

  const isRecurring =
    paymentData?.payThroughRecurrence || paymentData?.payThroughEMI

  const getIntervalLabel = () => {
    if (!isRecurring || !paymentData?.recurrenceObj) return ''
    const type = paymentData.recurrenceObj.intervalType?.toLowerCase() || ''
    if (type.includes('month')) return 'per month'
    if (type.includes('year') || type.includes('annual')) return 'per year'
    if (type.includes('week')) return 'per week'
    if (type.includes('day') || type.includes('daily')) return 'per day'
    return ''
  }

  const getBilledLabel = () => {
    if (!isRecurring || !paymentData?.recurrenceObj) return ''
    const type = paymentData.recurrenceObj.intervalType?.toLowerCase() || ''
    if (type.includes('month')) return 'billed monthly'
    if (type.includes('year') || type.includes('annual')) return 'billed annually'
    if (type.includes('week')) return 'billed weekly'
    if (type.includes('day') || type.includes('daily')) return 'billed daily'
    return ''
  }

  const appName =
    isArray(productDetails) && productDetails?.length > 0
      ? productDetails[0]?.name
      : null

  const hasUserDetails =
    paymentData?.name || paymentData?.mobile || paymentData?.email

  const hasAddress = paymentData?.address

  const formatAddress = (address) => {
    if (!address) return ''
    if (typeof address === 'string') return address
    const parts = [
      address?.line1 || address?.street,
      address?.line2,
      address?.city,
      address?.state,
      address?.postal_code || address?.zip,
      address?.country,
    ].filter(Boolean)
    return parts.join(', ')
  }

  const hasCharges =
    isArray(chargeData?.mainChargeData) &&
    !isEmpty(chargeData?.mainChargeData)

  // ─── Payment Link Flow: Single-column layout for Indian payments ───
  if (pl_code) {
    return (
      <Grid container style={{ justifyContent: 'center', padding: sm ? '16px 8px' : '32px 16px' }}>
        <Grid item xs={12} sm={10} md={8} lg={5}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
            padding: sm ? '24px 18px' : '32px 28px',
          }}>
            {/* Merchant row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              {merchantLogo ? (
                <img
                  src={merchantLogo}
                  alt="logo"
                  style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', border: '1px solid #eee' }}
                />
              ) : (
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: '#f0f4f8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <ListAlt style={{ fontSize: 24, color: themeColor }} />
                </div>
              )}
              <div>
                {isRecurring && (
                  <Typography style={{ fontSize: 11, color: '#8a96a8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Subscribing to
                  </Typography>
                )}
                <Typography style={{ fontSize: 16, fontWeight: 500, color: '#1a1a2e' }}>
                  {merchantName || appName || paymentData?.name || 'Payment'}
                </Typography>
              </div>
            </div>

            {/* Large amount */}
            <div>
              <Typography style={{ fontSize: sm ? 28 : 32, fontWeight: 500, color: '#1a1a2e', letterSpacing: '-0.5px', lineHeight: 1.1 }}>
                {currency_symbol} {formatAmt(finalAmount)}
              </Typography>
              {isRecurring && (
                <Typography style={{ fontSize: 13, color: '#8a96a8', marginTop: 4 }}>
                  {getIntervalLabel()} · {getBilledLabel()}
                </Typography>
              )}
            </div>

            {/* ─── User Details Section ─── */}
            {hasUserDetails && (
              <div style={{ marginTop: 24, background: '#f8fafc', borderRadius: 12, border: '1px solid #eef1f5', overflow: 'hidden' }}>
                <Typography style={{ fontSize: 12, fontWeight: 600, color: '#8a96a8', textTransform: 'uppercase', letterSpacing: 0.5, padding: '14px 20px 8px' }}>
                  User Details
                </Typography>
                {paymentData?.name && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid #eef1f5' }}>
                    <Typography style={{ fontSize: 13, color: '#8a96a8' }}>Name</Typography>
                    <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>{paymentData.name}</Typography>
                  </div>
                )}
                {paymentData?.mobile && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid #eef1f5' }}>
                    <Typography style={{ fontSize: 13, color: '#8a96a8' }}>Contact</Typography>
                    <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>{paymentData.mobile}</Typography>
                  </div>
                )}
                {paymentData?.email && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: hasAddress ? '1px solid #eef1f5' : 'none' }}>
                    <Typography style={{ fontSize: 13, color: '#8a96a8' }}>Email</Typography>
                    <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>{paymentData.email}</Typography>
                  </div>
                )}
                {hasAddress && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 20px' }}>
                    <Typography style={{ fontSize: 13, color: '#8a96a8' }}>Billing Address</Typography>
                    <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e', textAlign: 'right', maxWidth: '60%', lineHeight: 1.5 }}>
                      {formatAddress(paymentData.address)}
                    </Typography>
                  </div>
                )}
              </div>
            )}

            {/* ─── Price Breakdown Section ─── */}
            {order_details && isArray(order_details?.order_data) && order_details.order_data.length > 0 && (
              <div style={{ marginTop: 24, background: '#f8fafc', borderRadius: 12, border: '1px solid #eef1f5', overflow: 'hidden' }}>
                <Typography style={{ fontSize: 12, fontWeight: 600, color: '#8a96a8', textTransform: 'uppercase', letterSpacing: 0.5, padding: '14px 20px 8px' }}>
                  Price Breakdown
                </Typography>

                {order_details.order_data.map((item, index) => {
                  const itemTotal = (Number(item?.price) || 0) * (Number(item?.quantity) || 1)
                  return (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid #eef1f5' }}>
                      <div style={{ flex: 1 }}>
                        <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>
                          {item?.item}
                        </Typography>
                        {(item?.quantity > 1) && (
                          <Typography style={{ fontSize: 11, color: '#8a96a8', marginTop: 2 }}>
                            {item?.quantity} × {currency_symbol}{Number(item?.price || 0).toFixed(2)}
                          </Typography>
                        )}
                      </div>
                      <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>
                        {currency_symbol}{itemTotal.toFixed(2)}
                      </Typography>
                    </div>
                  )
                })}

                {/* Subtotal */}
                {order_details.order_data.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px', borderBottom: '1px solid #eef1f5' }}>
                    <Typography style={{ fontSize: 13, color: '#8a96a8' }}>Subtotal</Typography>
                    <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>
                      {currency_symbol}
                      {order_details.order_data
                        .reduce((sum, i) => sum + (Number(i?.price) || 0) * (Number(i?.quantity) || 1), 0)
                        .toFixed(2)}
                    </Typography>
                  </div>
                )}

                {/* Platform Fee */}
                {order_details?.platform_fee && Number(order_details.platform_fee.value) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px', borderBottom: '1px solid #eef1f5' }}>
                    <Typography style={{ fontSize: 13, color: '#8a96a8' }}>
                      {order_details.platform_fee.name || 'Platform Fee'}
                      {order_details.platform_fee.chargeAsPercentage && ` (${order_details.platform_fee.value}%)`}
                    </Typography>
                    <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>
                      {order_details.platform_fee.chargeAsPercentage
                        ? `${currency_symbol}${(
                            (Number(order_details.platform_fee.value) / 100) *
                            order_details.order_data.reduce((sum, i) => sum + (Number(i?.price) || 0) * (Number(i?.quantity) || 1), 0)
                          ).toFixed(2)}`
                        : `${currency_symbol}${Number(order_details.platform_fee.value).toFixed(2)}`}
                    </Typography>
                  </div>
                )}

                {/* Taxes */}
                {isArray(order_details?.taxes) &&
                  order_details.taxes
                    .filter((t) => Number(t?.amount) > 0)
                    .map((tax, idx, arr) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '10px 20px',
                          borderBottom: (idx < arr.length - 1 || (order_details?.roundoff && Number(order_details.roundoff) !== 0)) ? '1px solid #eef1f5' : 'none',
                        }}
                      >
                        <Typography style={{ fontSize: 13, color: '#8a96a8' }}>
                          {tax?.description || 'Tax'}
                        </Typography>
                        <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>
                          {currency_symbol}{Number(tax?.amount).toFixed(2)}
                        </Typography>
                      </div>
                    ))}

                {/* Round Off */}
                {order_details?.roundoff && Number(order_details.roundoff) !== 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px' }}>
                    <Typography style={{ fontSize: 13, color: '#8a96a8' }}>Round Off</Typography>
                    <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>
                      {order_details.roundOffSign === '-' ? '-' : '+'}{currency_symbol}{Number(order_details.roundoff).toFixed(2)}
                    </Typography>
                  </div>
                )}
              </div>
            )}

            {/* Amount Breakdown - only if extra charges exist and no order_details */}
            {hasCharges && !(order_details && isArray(order_details?.order_data) && order_details.order_data.length > 0) && (
              <div style={{ marginTop: 24, background: '#f8fafc', borderRadius: 12, border: '1px solid #eef1f5', overflow: 'hidden' }}>
                <Typography style={{ fontSize: 12, fontWeight: 600, color: '#8a96a8', textTransform: 'uppercase', letterSpacing: 0.5, padding: '14px 20px 8px' }}>
                  Amount Breakdown
                </Typography>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid #eef1f5' }}>
                  <Typography style={{ fontSize: 13, color: '#8a96a8' }}>Amount</Typography>
                  <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>
                    {checkNull(chargeData?.withoutChargeAmount?.toString())
                      ? `${currency_symbol}0`
                      : formatAmount(chargeData?.withoutChargeAmount, currency_symbol)}
                  </Typography>
                </div>
                {chargeData?.mainChargeData?.map((item, index) => {
                  const amountToAdd = paymentData?.chargeAsFixedAmount
                    ? item?.value
                    : (item?.value * paymentData?.amount) / 100
                  return item?.slug !== 'payment_gateway_fee' ? (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: index < chargeData.mainChargeData.length - 1 ? '1px solid #eef1f5' : 'none' }}>
                      <Typography style={{ fontSize: 13, color: '#8a96a8', textTransform: 'capitalize' }}>{item?.name}</Typography>
                      <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>
                        {currency_symbol}{amountToAdd?.toFixed(2)}
                      </Typography>
                    </div>
                  ) : null
                })}
              </div>
            )}

            {/* ─── Total Amount Row ─── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, padding: '12px 0' }}>
              <Typography style={{ fontSize: 15, fontWeight: 500, color: '#1a1a2e' }}>
                Total Amount
              </Typography>
              <Typography style={{ fontSize: 20, fontWeight: 700, color: themeColor }}>
                {currency_symbol}{formatAmt(finalAmount)}
              </Typography>
            </div>

            {/* ─── Pay Button ─── */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '14px 24px',
                borderRadius: 12,
                color: '#fff',
                fontWeight: 700,
                fontSize: 16,
                cursor: 'pointer',
                marginTop: 8,
                background: themeColor,
                justifyContent: showAmountInBtn ? 'space-between' : 'center',
              }}
              onClick={() => onClick()}
            >
              <span>{changeBtnText || 'Pay'}</span>
              {showAmountInBtn && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                  {formatAmount(finalAmount, currency_symbol)}
                  <ArrowForward style={{ fontSize: 18 }} />
                </span>
              )}
            </div>

          </div>
        </Grid>
      </Grid>
    )
  }

  // ─── Client Integration Flow: Minimal centered card ───
  return (
    <Grid
      item
      xs={12}
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{
        width: '100%',
        maxWidth: 420,
        padding: sm ? '24px 16px' : '32px 24px',
      }}>
        {/* Amount display */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <Typography style={{ fontSize: 13, color: '#8a96a8', fontWeight: 500, marginBottom: 6 }}>
            Amount to pay
          </Typography>
          <Typography style={{ fontSize: sm ? 32 : 38, fontWeight: 500, color: '#1a1a2e', letterSpacing: '-0.5px', lineHeight: 1.1 }}>
            {formatAmount(finalAmount, currency_symbol)}
          </Typography>
          {isRecurring && (
            <Typography style={{ fontSize: 13, color: '#8a96a8', marginTop: 6 }}>
              {getIntervalLabel()}
            </Typography>
          )}
        </div>

        {/* Charge breakdown - compact */}
        {hasCharges && (
          <div style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #eef1f5', overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #eef1f5' }}>
              <Typography style={{ fontSize: 13, color: '#8a96a8' }}>Amount</Typography>
              <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>
                {checkNull(chargeData?.withoutChargeAmount?.toString())
                  ? `${currency_symbol}0`
                  : formatAmount(chargeData?.withoutChargeAmount, currency_symbol)}
              </Typography>
            </div>
            {chargeData?.mainChargeData?.map((item, index) => {
              const amountToAdd = paymentData?.chargeAsFixedAmount
                ? item?.value
                : (item?.value * paymentData?.amount) / 100
              return item?.slug !== 'payment_gateway_fee' ? (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', borderBottom: index < chargeData.mainChargeData.length - 1 ? '1px solid #eef1f5' : 'none' }}>
                  <Typography style={{ fontSize: 13, color: '#8a96a8', textTransform: 'capitalize' }}>{item?.name}</Typography>
                  <Typography style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>
                    {currency_symbol}{amountToAdd?.toFixed(2)}
                  </Typography>
                </div>
              ) : null
            })}
          </div>
        )}

        {/* Description */}
        <Typography style={{ fontSize: 12, color: '#b0bac8', textAlign: 'center', lineHeight: 1.6, marginBottom: 20 }}>
          You'll be redirected to a secure payment gateway to complete your transaction.
        </Typography>

        {/* Pay Button - split layout: label left, amount + arrow right */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '14px 24px',
            borderRadius: 12,
            color: '#fff',
            fontWeight: 600,
            fontSize: 15,
            cursor: 'pointer',
            background: themeColor,
            justifyContent: showAmountInBtn ? 'space-between' : 'center',
          }}
          onClick={() => onClick()}
        >
          <span>{changeBtnText || 'Pay'}</span>
          {showAmountInBtn && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
              {formatAmount(finalAmount, currency_symbol)}
              <ArrowForward style={{ fontSize: 18 }} />
            </span>
          )}
        </div>

      </div>
    </Grid>
  )
}

export default PricingBreakdown
